import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { groupBy } from 'lodash';

import { IChangeProperty, IUserViewModel } from 'apis/nswag';

import { logout, saveLoginInfo } from 'features/auth/authSlice';

export interface UserDetailState {
  user?: IUserViewModel;
  activeClientId: number | null;
  changedProperties: IChangeProperty[];
}

export const initialState: UserDetailState = {
  user: undefined,
  activeClientId: null,
  changedProperties: [],
};

const userDetailSlice = createSlice({
  name: 'userDetail',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<IUserViewModel | undefined>) => {
      state.user = action.payload;
      // no client is active by default if there's more than one
      if (action.payload && action.payload.clients && action.payload.clients.length === 1) {
        const firstClient = action.payload.clients[0].id;
        console.log('Setting activeClientId to the only client id:', firstClient);
        state.activeClientId = firstClient;
      } else {
        state.activeClientId = null;
        console.log('More than one client found. No activeClientId set.');
      }
    },
    setActiveClientId: (state, action: PayloadAction<number | null>) => {
      state.activeClientId = action.payload;
    },
    setChangedProperties: (state, action: PayloadAction<IChangeProperty[]>) => {
      state.changedProperties = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.user = undefined;
      state.activeClientId = null;
    });
    builder.addCase(saveLoginInfo, (state, action) => {
      // the auth user model is different from user detail model, but we need only the 'clients' portion which is compatible
      state.user = action.payload.user?.toJSON() as any;
      state.activeClientId = null;
    });
  },
});

export const { setUser, setActiveClientId, setChangedProperties } = userDetailSlice.actions;

export const userDetailSelector = (state: { userDetail: UserDetailState }) => state.userDetail.user;

export const activeClientIdSelector = (state: { userDetail: UserDetailState }) => state.userDetail.activeClientId;

export const userClientsSelector = (state: { userDetail: UserDetailState }) => state.userDetail.user?.clients || [];

export const changedPropertiesSelector = (state: { userDetail: UserDetailState }) => {
  const groups = groupBy(state.userDetail.changedProperties, (p) => p.group);
  // first item of each group
  return Object.values(groups).map((g) => g[0]);
};

export default userDetailSlice.reducer;
